import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import { useNextEvents } from './useNextEvents';
import {
    Button,
    GenericError,
    Icon,
    PageLoader,
    TextField,
    Title,
} from '../common';
import { COLOURS } from '../../utils/consts';
import { formatVoteDateDistance, renderEventDate } from '../../utils/helpers';
import { useAppSelector } from '../../store/hooks';
import { useTranslation } from '../../hooks/translations/TranslationProvider';

import './nextEvents.css';
import { EVENT_STATUSES } from '../../utils/types';

const NextEvents = () => {
    const navigate = useLocalizedNavigate();
    const { isLoading, userEvents, error } = useNextEvents();
    const auth = useAppSelector(state => state.auth);
    const { t } = useTranslation();

    const language = auth.authData?.language;

    function eventClickHandler(eventId: string) {
        navigate(`/events/${eventId}`);
    }

    if (isLoading) {
        return <PageLoader />;
    }

    if (error) {
        return <GenericError error={error} />;
    }

    if (userEvents?.length === 0) {
        return (
            <div className="next-events page-container">
                <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                    <Title text={t('myEvents.noEvents')} size="xl" />
                </div>
                <div className="next-events-empty-actions">
                    <div
                        className="next-events-empty-action hover-green"
                        onClick={() => navigate('/finder')}
                    >
                        <div className="next-events-empty-action-icon-green">
                            <Icon
                                name="bx-map"
                                size="35px"
                                color={COLOURS.WHITE}
                            />
                        </div>
                        <div className="next-events-empty-action-text">
                            <Title text={t('common.search')} size="l" />
                            <TextField color="subtitle">
                                {t('myEvents.search')}
                            </TextField>
                        </div>
                    </div>
                    <div
                        className="next-events-empty-action hover-orange"
                        onClick={() => navigate('/create')}
                    >
                        <div className="next-events-empty-action-icon-orange">
                            <Icon
                                name="bx-calendar"
                                size="35px"
                                color={COLOURS.WHITE}
                            />
                        </div>
                        <div className="next-events-empty-action-text">
                            <Title text={t('common.create')} size="l" />
                            <TextField color="subtitle">
                                {t('myEvents.create')}
                            </TextField>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="next-events page-container">
            <div style={{ marginBottom: '5px' }}>
                <Title text={t('myEvents.title')} size="xl" />
            </div>
            {userEvents?.map(event => (
                <div className="next-event" key={event.id}>
                    <div
                        className={`next-event-header ${event.type.toLowerCase()}`}
                    >
                        <div className="pill">
                            <Icon
                                name={`bx-${event.type.toLowerCase()}`}
                                size="10px"
                            />
                            <TextField size="s" color="subtitle" weight="bold">
                                {t(`eventTypes.${event.type.toLowerCase()}`)}
                            </TextField>
                        </div>
                        {event.status === EVENT_STATUSES.VOTE_PHASE && (
                            <div className="pill pill-light-green">
                                <TextField size="s" color="green" weight="bold">
                                    {`${t(
                                        'common.voteEnds'
                                    )} ${formatVoteDateDistance(
                                        event.voteEndDate!,
                                        language
                                    )}`}
                                </TextField>
                            </div>
                        )}
                    </div>
                    <div className="next-event-content">
                        <TextField weight="bold">{event.place}</TextField>
                        <div className="next-event-content-row">
                            <Icon
                                name="bx-calendar"
                                size="22px"
                                color={COLOURS.SUBTITLE}
                            />
                            <TextField size="s" color="subtitle">
                                {renderEventDate(event.date, language, {
                                    todayLabel: t('date.today'),
                                    atLabel: t('date.at'),
                                    showTime: true,
                                })}
                            </TextField>
                        </div>
                        <div className="next-event-content-row">
                            <Icon
                                name="bx-group"
                                size="22px"
                                color={COLOURS.SUBTITLE}
                            />
                            <TextField size="s" color="subtitle">
                                {`${event.joinedUsers}/${event.maxUsers}`}
                            </TextField>
                        </div>
                        <div className="next-event-content-action">
                            <Button
                                text={t('common.viewEvent')}
                                size="lg"
                                variant="green"
                                onClick={() => eventClickHandler(event.id)}
                            />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default NextEvents;
